/**
 * TaskEmptyState Component
 *
 * Empty, loading, and error states for the Tasks inbox.
 */

import { Box, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { IconPlus, IconRefresh } from '@tabler/icons-react';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';

interface TaskEmptyStateProps {
  variant: 'no-tasks' | 'no-results' | 'loading' | 'error';
  hasFilters?: boolean;
  onCreateTask?: () => void;
  onClearFilters?: () => void;
  onRetry?: () => void;
  errorMessage?: string;
}

export function TaskEmptyState({
  variant,
  hasFilters = false,
  onCreateTask,
  onClearFilters,
  onRetry,
  errorMessage,
}: TaskEmptyStateProps): JSX.Element {
  switch (variant) {
    case 'loading':
      return <LoadingState />;
    case 'error':
      return <ErrorState errorMessage={errorMessage} onRetry={onRetry} />;
    case 'no-results':
      return <NoResultsState hasFilters={hasFilters} onClearFilters={onClearFilters} onCreateTask={onCreateTask} />;
    case 'no-tasks':
    default:
      return <NoTasksState onCreateTask={onCreateTask} />;
  }
}

function LoadingState(): JSX.Element {
  return (
    <Box p="xl" style={{ minHeight: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Stack align="center" gap="md">
        <Loader size="lg" />
        <Text c="dimmed" size="sm">
          Loading tasks...
        </Text>
      </Stack>
    </Box>
  );
}

function ErrorState({
  errorMessage,
  onRetry,
}: {
  errorMessage?: string;
  onRetry?: () => void;
}): JSX.Element {
  return (
    <Box p="xl" style={{ minHeight: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Stack align="center" gap="md">
        <Title order={4} ta="center">
          Unable to load tasks
        </Title>
        <Text c="dimmed" size="sm" ta="center" maw={300}>
          {errorMessage || 'Something went wrong while loading your tasks. Please try again.'}
        </Text>
        {onRetry && (
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={onRetry}
          >
            Try Again
          </Button>
        )}
      </Stack>
    </Box>
  );
}

function NoResultsState({
  hasFilters,
  onClearFilters,
  onCreateTask,
}: {
  hasFilters?: boolean;
  onClearFilters?: () => void;
  onCreateTask?: () => void;
}): JSX.Element {
  return (
    <Box p="xl" style={{ minHeight: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Stack align="center" gap="md">
        <Title order={4} ta="center">
          No tasks found
        </Title>
        <Text c="dimmed" size="sm" ta="center" maw={300}>
          {hasFilters
            ? 'No tasks match your current filters. Try adjusting or clearing your filters.'
            : 'Your search didn\'t match any tasks.'}
        </Text>
        <Group gap="sm">
          {hasFilters && onClearFilters && (
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={onClearFilters}
            >
              Clear Filters
            </Button>
          )}
          {onCreateTask && (
            <Group gap="xs">
              <Button leftSection={<IconPlus size={16} />} onClick={onCreateTask}>
                Create Task
              </Button>
              <LocalhostOnlyBadge />
            </Group>
          )}
        </Group>
      </Stack>
    </Box>
  );
}

function NoTasksState({
  onCreateTask,
}: {
  onCreateTask?: () => void;
}): JSX.Element {
  return (
    <Box p="xl" style={{ minHeight: 280, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Stack align="center" gap="lg">
        <Stack gap="xs" align="center">
          <Title order={3} ta="center">
            All caught up!
          </Title>
          <Text c="dimmed" size="sm" ta="center" maw={320}>
            You don&apos;t have any tasks right now. When tasks are assigned to you or created, they&apos;ll appear here.
          </Text>
        </Stack>
        {onCreateTask && (
          <Group gap="xs">
            <Button leftSection={<IconPlus size={16} />} onClick={onCreateTask} size="md">
              Create Your First Task
            </Button>
            <LocalhostOnlyBadge />
          </Group>
        )}
      </Stack>
    </Box>
  );
}
